import { API_URL } from '../config';
import { t, type DictKey } from '../i18n';
import { uk } from '../i18n/uk';
import { noteApiError } from '../lib/clientErrors';

/** Помилка API у формі контракту `{error, detail}`. status=0 — мережева помилка. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string | null;
  /** Готовий текст для людини (напр. перекладена помилка перевірки полів); має перевагу в errorText. */
  userText?: string;

  constructor(status: number, code: string, detail: string | null = null, userText?: string) {
    super(detail ? `${code}: ${detail}` : code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    if (userText) this.userText = userText;
  }

  /** Лише 404 — корисно для ендпоінтів, яких бекенд ще не реалізував. */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue | QueryValue[]>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Повернути сирий Response (для PDF тощо). */
  raw?: boolean;
}

type UnauthorizedHandler = (path: string) => void;

/**
 * Токен з /auth/login — сесія саме цієї вкладки. Cookie спільна для всіх вкладок браузера: вхід іншою людиною
 * або «Вийти» в сусідній вкладці її перезаписує/стирає, і після оновлення сторінки ця вкладка опинялась на /login
 * (або під чужим іменем). Тому токен лежить ще й у sessionStorage — він живе лише в цій вкладці й до її закриття.
 */
const TAB_TOKEN_KEY = 'xq.crm.tab-session';

function readTabToken(): string | null {
  try {
    return window.sessionStorage.getItem(TAB_TOKEN_KEY);
  } catch {
    return null;
  }
}

let bearerToken: string | null = readTabToken();
let onUnauthorized: UnauthorizedHandler | null = null;
let baseUrl = API_URL;

export function setBearerToken(token: string | null): void {
  bearerToken = token;
  try {
    if (token) window.sessionStorage.setItem(TAB_TOKEN_KEY, token);
    else window.sessionStorage.removeItem(TAB_TOKEN_KEY);
  } catch {
    /* sessionStorage недоступний — лишається памʼять і cookie */
  }
}

export function hasBearerToken(): boolean {
  return !!bearerToken;
}

export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

/** Для тестів. */
export function setBaseUrl(url: string): void {
  baseUrl = url.replace(/\/+$/, '');
}

export function buildUrl(path: string, query?: Query): string {
  const url = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) {
      if (v === undefined || v === null || v === '') continue;
      params.append(key, typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

interface FastApiValidationItem {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
  ctx?: Record<string, unknown>;
}

/** Помилка перевірки полів Pydantic (англ.) → одне речення українською з назвою поля, якщо її знаємо. */
export function validationText(items: FastApiValidationItem[]): string {
  const first = items[0];
  if (!first) return t('error.v.requiredAny');
  const path = (first.loc ?? []).filter((p) => p !== 'body' && p !== 'query' && typeof p === 'string') as string[];
  const name = path[path.length - 1];
  const fieldKey = `field.${name}` as DictKey;
  const field = name && fieldKey in uk ? t(fieldKey) : null;
  const msg = first.msg ?? '';
  const type = first.type ?? '';
  const num = (k: string) => Number(first.ctx?.[k] ?? /(\d+)/.exec(msg)?.[1] ?? 0);
  if (!field) return t('error.validation');
  if (type === 'string_too_short' || /should have at least|at least \d+ char/i.test(msg)) return t('error.v.minLength', { field, n: num('min_length') });
  if (type === 'string_too_long' || /should have at most/i.test(msg)) return t('error.v.maxLength', { field, n: num('max_length') });
  if (type === 'missing' || /field required/i.test(msg)) return t('error.v.required', { field });
  if (/value is not a valid|input should be|invalid/i.test(msg) || type) return t('error.v.invalid', { field });
  return t('error.validation');
}

/** Нормалізує тіло помилки: контракт `{error, detail}`, FastAPI `{detail: [...]}` або текст. */
export async function parseError(res: Response): Promise<ApiError> {
  let data: unknown = null;
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.error === 'string') {
      const detail = obj.detail == null ? null : typeof obj.detail === 'string' ? obj.detail : JSON.stringify(obj.detail);
      // гард чекліста: сервер перелічує відкриті обовʼязкові пункти — показуємо, яких саме документів бракує
      if (Array.isArray(obj.open_items) && obj.open_items.length > 0) {
        const names = (obj.open_items as { name?: string }[]).map((i) => i.name).filter(Boolean);
        const list = names.slice(0, 12).join(', ') + (names.length > 12 ? ' …' : '');
        return new ApiError(res.status, obj.error, detail, t('error.guardChecklistList', { n: obj.open_items.length, items: list }));
      }
      return new ApiError(res.status, obj.error, detail);
    }
    if (Array.isArray(obj.detail)) {
      const msg = (obj.detail as FastApiValidationItem[])
        .map((d) => `${(d.loc ?? []).filter((p) => p !== 'body').join('.')}: ${d.msg ?? ''}`.trim())
        .join('; ');
      return new ApiError(res.status, 'validation', msg || null, validationText(obj.detail as FastApiValidationItem[]));
    }
    if (typeof obj.detail === 'string') {
      return new ApiError(res.status, defaultCode(res.status), obj.detail);
    }
  }
  return new ApiError(res.status, defaultCode(res.status), typeof data === 'string' && data ? data.slice(0, 300) : null);
}

function defaultCode(status: number): string {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'validation';
    case 429:
      return 'rate_limited';
    default:
      return status >= 500 ? 'server_error' : 'unknown';
  }
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData || opts.body instanceof Blob) {
      body = opts.body;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
  }
  if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'network', e instanceof Error ? e.message : null);
  }

  if (!res.ok) {
    const err = await parseError(res);
    noteApiError(method, path, res.status, err.code);
    if (res.status === 401 && err.code === 'unauthorized' && onUnauthorized) onUnauthorized(path);
    throw err;
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export const http = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, { body: body ?? {}, query }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body: body ?? {} }),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** Пряме завантаження файлу за presigned URL (без cookie — це інший origin сховища). */
export async function putFile(
  url: string,
  file: Blob,
  headers: Record<string, string> = {},
  method = 'PUT',
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { method, body: file, headers, credentials: 'omit' });
  } catch (e) {
    throw new ApiError(0, 'upload_failed', e instanceof Error ? e.message : null);
  }
  if (!res.ok) throw new ApiError(res.status, 'upload_failed', `HTTP ${res.status}`);
}
